<template>
  <AppMenu>
    <template #activator="{ toggle }">
      <AppButton color="blue" aria-haspopup="menu" @click="toggle">
        Förvaltningsåtgärder
        <template #append>
          <SvgIcon :path="mdiChevronDown" size="16" />
        </template>
      </AppButton>
    </template>

    <template #default="{ close }">
      <div class="menu-panel" role="menu">
        <button
          v-for="action in debtServicingActions"
          :key="action.value"
          type="button"
          class="menu-item"
          role="menuitem"
          @click.stop="(openAction(action), close())"
        >
          {{ action.label }}
        </button>
      </div>
    </template>
  </AppMenu>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { mdiChevronDown } from '@mdi/js'
import { AppButton, AppMenu, SvgIcon, useModal } from '@staff/ui'
import type { Debt } from '../debt.types'
import ChangeAmortizationModal from './ChangeAmortizationModal.vue'
import InterestRateLockModal from './InterestRateLockModal.vue'

type DebtServicingActionValue =
  | 'amortization-proof'
  | 'extra-amortization'
  | 'interest-rate-lock'
  | 'change-amortization'
  | 'topup-application'

type DebtServicingAction = {
  value: DebtServicingActionValue
  label: string
}

const debtServicingActions: DebtServicingAction[] = [
  { value: 'interest-rate-lock', label: 'Räntebindning och villkor' },
  { value: 'change-amortization', label: 'Månadsamortering' },
  { value: 'topup-application', label: 'Ny ansökan för att utöka bolånet' },
]

const props = defineProps<{ debt: Debt }>()

// DebtLayout owns the debt ref and passes it down, so a save has to travel back up.
const emit = defineEmits<{ saved: [debt: Debt] }>()

const router = useRouter()
const { openModal } = useModal()

// Only the actions listed in debtServicingActions are reachable from the menu.
const openAction = (action: DebtServicingAction) => {
  if (action.value === 'topup-application') {
    void router.push({ name: 'ny-ansokan', query: { typ: 'topupLoanApplication', debtId: props.debt.id } })
  }

  if (action.value === 'interest-rate-lock') {
    openModal(
      action.label,
      InterestRateLockModal,
      {
        debt: props.debt,
        onSaved: (updatedDebt: Debt) => {
          emit('saved', updatedDebt)
        },
      },
      { size: 'large' },
    )
  }

  if (action.value === 'change-amortization') {
    openModal(
      action.label,
      ChangeAmortizationModal,
      {
        debt: props.debt,
        onSaved: (updatedDebt: Debt) => {
          emit('saved', updatedDebt)
        },
      },
      { size: 'large' },
    )
  }
}
</script>

<style scoped lang="scss">
.menu-panel {
  min-width: 16rem;
  padding-block: var(--space-4);
  overflow: hidden;
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-md);
  background-color: var(--color-canvas-default);
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 10%),
    0 4px 6px -4px rgb(0 0 0 / 10%);
}

.menu-item {
  display: block;
  width: 100%;
  padding: var(--space-8) var(--space-16);
  border: 0;
  background: none;
  text-align: left;
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  font-weight: var(--font-weight-medium);
  color: var(--color-fg-default);
  cursor: pointer;

  &:hover,
  &:focus {
    background-color: var(--color-canvas-hover);
  }

  &:focus {
    outline: none;
  }
}
</style>
